import * as Ably from 'ably'
import { realtimeService } from 'src/services/realtime'
import { IPresenceTabState, toWireData } from './types'

const RETRY_MIN_MS = 5_000
const RETRY_MAX_MS = 60_000

/**
 * Keeps this tab in the Ably presence set of the partner's membership channel.
 *
 * The channel is attached with the PRESENCE mode only: the tab is present, but receives nothing
 * about teammates (what they may see is decided and broadcast by the backend). The price is that
 * ably-js cannot re-enter on its own any more (it learns about its own membership from the echo
 * it no longer gets), so everything goes through one idempotent `sync()`:
 * `presence.update()` enters when the member is absent and updates when it is there.
 *
 * The channel name comes from the backend (`GET /user` → presence.channel) and is NOT prefixed
 * with the partner like the other realtime channels, so it never goes through
 * `realtimeService.getChannel()`.
 */
export class PresenceClient {
  private channel?: Ably.RealtimeChannel
  private state?: IPresenceTabState
  private paused = false
  private syncing = false
  private dirty = false
  private retryDelayMs = RETRY_MIN_MS
  private retryTimer?: ReturnType<typeof setTimeout>

  start(channelName: string, state: IPresenceTabState) {
    this.state = state

    if (this.channel) {
      this.sync()

      return
    }

    const client = realtimeService.client
    if (!client) return

    try {
      this.channel = client.channels.get(channelName, { modes: ['PRESENCE'] })
    } catch (e) {
      // the channel object already exists with other options
      this.channel = client.channels.get(channelName)
      this.channel.setOptions({ modes: ['PRESENCE'] }).catch(console.debug)
    }

    // Ably drops the member ~15 s after the transport is lost, even if the connection is
    // resumed later, so every (re)attach has to be followed by a sync
    this.channel.on('attached', this.sync)
    this.channel.on('update', this.sync)

    this.sync()
  }

  /** New state of the tab; sent if it differs from what Ably has */
  update(state: IPresenceTabState) {
    this.state = state
    this.sync()
  }

  /** Step out of presence but keep everything ready to come back (long idle tab) */
  pause() {
    if (this.paused) return
    this.paused = true
    this.clearRetry()
    this.channel?.presence.leave().catch(console.debug)
  }

  resume() {
    if (!this.paused) return
    this.paused = false
    this.sync()
  }

  isPaused() {
    return this.paused
  }

  sync = () => {
    if (!this.channel || !this.state || this.paused) return

    // one request at a time; whatever changed meanwhile goes out right after it
    if (this.syncing) {
      this.dirty = true

      return
    }

    this.syncing = true
    this.dirty = false
    this.clearRetry()

    this.channel.presence
      .update(toWireData(this.state))
      .then(() => {
        this.retryDelayMs = RETRY_MIN_MS
      })
      .catch((error) => {
        // e.g. the presence set of the channel is full, or the token has no presence capability yet
        console.debug('presence: sync failed', error)
        this.retryTimer = setTimeout(this.sync, this.retryDelayMs)
        this.retryDelayMs = Math.min(this.retryDelayMs * 2, RETRY_MAX_MS)
      })
      .finally(() => {
        this.syncing = false
        if (this.dirty) this.sync()
      })
  }

  /** Leave for good (logout). Resolves when Ably confirmed it or after `timeoutMs` */
  stop(timeoutMs = 1000): Promise<void> {
    const channel = this.channel

    this.clearRetry()
    this.channel = undefined
    this.state = undefined
    this.paused = false

    if (!channel) return Promise.resolve()

    channel.off('attached', this.sync)
    channel.off('update', this.sync)

    return Promise.race([
      channel.presence.leave().catch(console.debug) as Promise<void>,
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ])
  }

  private clearRetry() {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = undefined
  }
}
